import React from 'react';
import { Container, Typography, Box, List, ListItem } from '@mui/material';
import PageHead from '../components/PageHead';

const PrivacyPolicy = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <PageHead 
        title="Privacy Policy" 
        description="AutoBlogPilot's privacy policy and data protection information"
      />
      <Typography variant="h3" component="h1" gutterBottom>
        Privacy Policy
      </Typography>
      
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Effective Date: December 1, 2024
      </Typography>

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          1. Introduction
        </Typography>
        <Typography paragraph>
          Welcome to AutoBlogPilot ("we," "us," or "our"). We are committed to protecting your privacy and ensuring that your personal information is handled in a safe and responsible manner. This Privacy Policy describes how we collect, use, disclose, and safeguard your information when you use our automated blogging and SEO platform available at autoblogpilot.com (the "Service"). By accessing or using our Service, you agree to this Privacy Policy.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          2. Information We Collect
        </Typography>
        <Typography paragraph>
          We may collect personal and non-personal information about you in various ways:
        </Typography>

        <Typography variant="h6" gutterBottom>
          2.1 Personal Information
        </Typography>
        <Box sx={{ pl: 2 }}>
          <Typography component="div" gutterBottom>
            <strong>Account Information</strong>
            <List dense>
              <ListItem>Email address</ListItem>
              <ListItem>Username or display name</ListItem>
              <ListItem>Password (securely hashed)</ListItem>
            </List>
          </Typography>

          <Typography component="div" gutterBottom>
            <strong>Billing Information</strong>
            <List dense>
              <ListItem>Full name</ListItem>
              <ListItem>Billing address</ListItem>
              <ListItem>Payment information (processed via secure third-party payment processors)</ListItem>
            </List>
          </Typography>

          <Typography component="div" gutterBottom>
            <strong>Blog/Website Information</strong>
            <List dense>
              <ListItem>URLs of your blogs or websites</ListItem>
              <ListItem>API keys or codes used to connect our WordPress app</ListItem>
              <ListItem>Information about the installation status of our WordPress app</ListItem>
              <ListItem>Content preferences and settings</ListItem>
            </List>
          </Typography>

          <Typography component="div" gutterBottom>
            <strong>Professional Information (if provided)</strong>
            <List dense>
              <ListItem>Company name</ListItem>
              <ListItem>Industry</ListItem>
              <ListItem>Job title</ListItem>
            </List>
          </Typography>

          <Typography component="div" gutterBottom>
            <strong>Communication Preferences</strong>
            <List dense>
              <ListItem>Notification settings</ListItem>
              <ListItem>Marketing and communication preferences</ListItem>
            </List>
          </Typography>
        </Box>

        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
          2.2 Non-Personal Information
        </Typography>
        <Box sx={{ pl: 2 }}>
          <Typography component="div" gutterBottom>
            <strong>Automatically Collected Information</strong>
            <List dense>
              <ListItem>IP address</ListItem>
              <ListItem>Browser type and version</ListItem>
              <ListItem>Device information (e.g., device identifiers, operating system)</ListItem>
              <ListItem>Time zone settings</ListItem>
              <ListItem>Log files and usage data</ListItem>
            </List>
          </Typography>

          <Typography component="div" gutterBottom>
            <strong>Cookies and Similar Technologies</strong>
            <Typography paragraph sx={{ pl: 2 }}>
              We use cookies, web beacons, and similar technologies to collect information about your interaction with our Service.
            </Typography>
          </Typography>

          <Typography component="div" gutterBottom>
            <strong>Analytics Data</strong>
            <List dense>
              <ListItem>Interaction metrics</ListItem>
              <ListItem>Performance data</ListItem>
              <ListItem>Blog performance metrics and SEO rankings</ListItem>
            </List>
          </Typography>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          3. How We Use Your Information
        </Typography>
        <Typography paragraph>
          We use the information we collect for various purposes, including:
        </Typography>
        <Box sx={{ pl: 2 }}>
          <Typography component="div" gutterBottom>
            <strong>Service Provision</strong>
            <List dense>
              <ListItem>To create and manage your account</ListItem>
              <ListItem>To provide and maintain the Service</ListItem>
              <ListItem>To process transactions and billing</ListItem>
            </List>
          </Typography>

          <Typography component="div" gutterBottom>
            <strong>Communication</strong>
            <List dense>
              <ListItem>To send administrative information like updates, security alerts, and support messages</ListItem>
              <ListItem>To respond to your inquiries and provide customer support</ListItem>
            </List>
          </Typography>

          <Typography component="div" gutterBottom>
            <strong>Personalization and Improvement</strong>
            <List dense>
              <ListItem>To personalize your experience and deliver content relevant to your interests</ListItem>
              <ListItem>To monitor and analyze usage and trends to improve the Service</ListItem>
            </List>
          </Typography>

          <Typography component="div" gutterBottom>
            <strong>Marketing</strong>
            <List dense>
              <ListItem>To send marketing and promotional communications (where consent is obtained)</ListItem>
            </List>
          </Typography>

          <Typography component="div" gutterBottom>
            <strong>Legal and Security</strong>
            <List dense>
              <ListItem>To enforce our terms and conditions</ListItem>
              <ListItem>To comply with legal obligations</ListItem>
              <ListItem>To protect the rights, property, and safety of AutoBlogPilot, our users, or others</ListItem>
            </List>
          </Typography>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          4. Legal Basis for Processing (EEA Users)
        </Typography>
        <Typography paragraph>
          If you are located in the European Economic Area, we process your personal information based on the following legal grounds:
        </Typography>
        <Box sx={{ pl: 2 }}>
          <List dense>
            <ListItem>
              <strong>Consent</strong> - Where you have given consent for processing your personal data.
            </ListItem>
            <ListItem>
              <strong>Performance of a Contract</strong> - To provide the Service as per our agreement with you.
            </ListItem>
            <ListItem>
              <strong>Legitimate Interests</strong> - For our legitimate interests in improving the Service and providing a secure platform.
            </ListItem>
            <ListItem>
              <strong>Compliance with Legal Obligations</strong> - To comply with legal and regulatory requirements.
            </ListItem>
          </List>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          5. Information Sharing and Disclosure
        </Typography>
        <Typography paragraph>
          We may share your information in the following circumstances:
        </Typography>

        <Typography variant="h6" gutterBottom>
          5.1 With Service Providers
        </Typography>
        <Box sx={{ pl: 2 }}>
          <Typography component="div" gutterBottom>
            <strong>Third-Party Vendors</strong>
            <List dense>
              <ListItem>Payment processors</ListItem>
              <ListItem>Data analysis providers</ListItem>
              <ListItem>Email delivery services</ListItem>
              <ListItem>Hosting services</ListItem>
              <ListItem>Customer service providers</ListItem>
              <ListItem>Marketing assistance</ListItem>
            </List>
          </Typography>
        </Box>

        <Typography variant="h6" gutterBottom>
          5.2 With Content Generation Providers
        </Typography>
        <Box sx={{ pl: 2 }}>
          <Typography component="div" gutterBottom>
            <strong>AI and Content Services</strong>
            <Typography paragraph>
              OpenAI, Anthropic, and similar service providers used for content generation.
            </Typography>
          </Typography>
        </Box>

        <Typography variant="h6" gutterBottom>
          5.3 For Legal Reasons
        </Typography>
        <Box sx={{ pl: 2 }}>
          <Typography component="div" gutterBottom>
            <strong>Compliance</strong>
            <Typography paragraph>
              If required by law or in response to legal processes, such as a court order or subpoena.
            </Typography>
          </Typography>
          <Typography component="div" gutterBottom>
            <strong>Protection</strong>
            <Typography paragraph>
              To protect the rights, property, and safety of AutoBlogPilot, our users, or others.
            </Typography>
          </Typography>
        </Box>

        <Typography variant="h6" gutterBottom>
          5.4 In Business Transfers
        </Typography>
        <Box sx={{ pl: 2 }}>
          <Typography component="div" gutterBottom>
            <strong>Corporate Transactions</strong>
            <Typography paragraph>
              In connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition.
            </Typography>
          </Typography>
        </Box>

        <Typography variant="h6" gutterBottom>
          5.5 With Your Consent
        </Typography>
        <Box sx={{ pl: 2 }}>
          <Typography component="div" gutterBottom>
            <strong>Authorized Sharing</strong>
            <Typography paragraph>
              We may share your information with third parties when you have given us explicit consent to do so.
            </Typography>
          </Typography>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          6. Data Retention
        </Typography>
        <Typography paragraph>
          We will retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          7. Data Security
        </Typography>
        <Typography paragraph>
          We implement a variety of security measures to maintain the safety of your personal information, including:
        </Typography>
        <Box sx={{ pl: 2 }}>
          <Typography component="div" gutterBottom>
            <strong>Encryption</strong>
            <List dense>
              <ListItem>Data in transit is encrypted using SSL/TLS protocols.</ListItem>
              <ListItem>Sensitive data is encrypted at rest where appropriate.</ListItem>
            </List>
          </Typography>
          <Typography component="div" gutterBottom>
            <strong>Access Controls</strong>
            <List dense>
              <ListItem>Restricted access to personal information.</ListItem>
              <ListItem>Regular security assessments and monitoring.</ListItem>
            </List>
          </Typography>
          <Typography component="div" gutterBottom>
            <strong>Secure Hosting</strong>
            <Typography paragraph>
              Data is stored on secure servers located in the United States (Digital Ocean).
            </Typography>
          </Typography>
        </Box>
        <Typography paragraph>
          Please note that no method of transmission over the internet or electronic storage is completely secure, and we cannot guarantee absolute security.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          8. International Data Transfers
        </Typography>
        <Typography paragraph>
          Your information may be transferred to, and maintained on, servers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ. By providing your information, you consent to such transfers.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          9. Your Rights and Choices
        </Typography>

        <Typography variant="h6" gutterBottom>
          9.1 European Economic Area (EEA) Residents
        </Typography>
        <Typography paragraph>
          Under the General Data Protection Regulation (GDPR), you have the right to:
        </Typography>
        <Box sx={{ pl: 2 }}>
          <List dense>
            <ListItem>
              <strong>Access</strong> - Request access to your personal data.
            </ListItem>
            <ListItem>
              <strong>Rectification</strong> - Correct inaccurate or incomplete data.
            </ListItem>
            <ListItem>
              <strong>Erasure</strong> - Request deletion of your personal data.
            </ListItem>
            <ListItem>
              <strong>Restriction</strong> - Restrict processing of your data.
            </ListItem>
            <ListItem>
              <strong>Data Portability</strong> - Receive a copy of your data in a commonly used format.
            </ListItem>
            <ListItem>
              <strong>Objection</strong> - Object to processing based on legitimate interests.
            </ListItem>
            <ListItem>
              <strong>Withdraw Consent</strong> - Withdraw consent at any time where processing is based on consent.
            </ListItem>
          </List>
        </Box>
        <Typography paragraph>
          To exercise these rights, please contact us using the information provided in Section 14.
        </Typography>

        <Typography variant="h6" gutterBottom>
          9.2 California Residents
        </Typography>
        <Typography paragraph>
          Under the California Consumer Privacy Act (CCPA), California residents have the right to:
        </Typography>
        <Box sx={{ pl: 2 }}>
          <List dense>
            <ListItem>
              <strong>Know</strong> - Request disclosure of the categories and specific pieces of personal information collected.
            </ListItem>
            <ListItem>
              <strong>Delete</strong> - Request deletion of personal information.
            </ListItem>
            <ListItem>
              <strong>Opt-Out</strong> - Opt-out of the sale of personal information (Note: We do not sell personal information).
            </ListItem>
            <ListItem>
              <strong>Non-Discrimination</strong> - Not be discriminated against for exercising CCPA rights.
            </ListItem>
          </List>
        </Box>
        <Typography paragraph>
          To exercise these rights, please contact us using the information provided in Section 14.
        </Typography>

        <Typography variant="h6" gutterBottom>
          9.3 All Users
        </Typography>
        <Box sx={{ pl: 2 }}>
          <List dense>
            <ListItem>
              <strong>Marketing Communications</strong> - Opt-out by following the unsubscribe instructions in our emails or contacting us.
            </ListItem>
            <ListItem>
              <strong>Cookies</strong> - Adjust your browser settings to refuse cookies or alert you when cookies are being sent.
            </ListItem>
          </List>
        </Box>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          10. Cookies and Tracking Technologies
        </Typography>
        <Typography paragraph>
          We use cookies and similar tracking technologies to track activity on our Service. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, some parts of our Service may not function properly if you disable cookies.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          11. Third-Party Services
        </Typography>
        <Typography paragraph>
          Our Service may contain links to third-party websites or services not owned or controlled by AutoBlogPilot. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          12. Children's Privacy
        </Typography>
        <Typography paragraph>
          Our Service is not intended for individuals under the age of 13. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal data from a child under 13 without parental consent, we will take steps to remove that information.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          13. Changes to This Privacy Policy
        </Typography>
        <Typography paragraph>
          We may update our Privacy Policy from time to time. We will notify you of any changes by updating the "Effective Date" at the top of this Privacy Policy. You are advised to review this Privacy Policy periodically for any changes.
        </Typography>

        <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>
          14. Contact Us
        </Typography>
        <Typography paragraph>
          If you have any questions about this Privacy Policy or our privacy practices, please contact us at:
        </Typography>
        <Typography component="div" sx={{ pl: 2 }}>
          <Typography paragraph>
            Email: contact@autoblogpilot.com
          </Typography>
        </Typography>
      </Box>
    </Container>
  );
};

export default PrivacyPolicy; 